import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const empty = { name: "", email: "", phone: "", address: "" };
const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";

export default function Vendors() {
  const { profiles, activeProfileId, setActiveProfileId } = useProfiles();
  const profileId = activeProfileId || profiles[0]?.id;
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    if (!profileId) { setVendors([]); return; }
    const { data } = await api.get(`/vendors?profile_id=${profileId}`);
    setVendors(data);
  }, [profileId]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...empty, ...v }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    if (!profileId) { toast.error("Select a business first"); return; }
    if (editing) await api.put(`/vendors/${editing.id}`, form);
    else await api.post(`/vendors?profile_id=${profileId}`, form);
    toast.success(editing ? "Vendor updated" : "Vendor created");
    setOpen(false); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete vendor?")) return;
    await api.delete(`/vendors/${id}`);
    toast.success("Vendor deleted");
    load();
  };

  return (
    <div className="px-4 sm:px-6 lg:px-10 py-6 lg:py-10">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8 lg:mb-12">
        <div>
          <p className="font-mont text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3">— Payees</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">Vendors</h1>
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {profiles.length > 1 && (
            <Select value={profileId} onValueChange={setActiveProfileId}>
              <SelectTrigger className="bg-transparent border border-white/20 rounded-none text-white font-mont w-full sm:w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white rounded-none">
                {profiles.map((p) => (<SelectItem key={p.id} value={p.id} className="font-mont text-sm">{p.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-vendor-btn" onClick={openNew} className="rounded-none bg-white text-black hover:bg-neutral-200 px-6 py-5 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" strokeWidth={2} /> New Vendor
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#121212] border-white/10 rounded-none text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">{editing ? "Edit Vendor" : "New Vendor"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              {[["Name", "name"], ["Email", "email"], ["Phone", "phone"]].map(([label, key]) => (
                <div key={key}>
                  <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">{label}</Label>
                  <Input data-testid={`vendor-${key}-input`} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className={fieldCls} />
                </div>
              ))}
              <div>
                <Label className="font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">Address</Label>
                <Textarea data-testid="vendor-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-transparent border border-white/20 rounded-none focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont" />
              </div>
              <Button data-testid="save-vendor-btn" onClick={save} className="w-full rounded-none bg-white text-black hover:bg-neutral-200">Save Vendor</Button>
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="border border-white/10">
        <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-white/10 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500">
          <div className="col-span-4">Name</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-3">Phone</div>
          <div className="col-span-2 text-right">—</div>
        </div>
        {vendors.length === 0 ? (
          <div className="py-20 text-center font-mont text-xs uppercase tracking-[0.3em] text-neutral-600">No vendors yet</div>
        ) : (
          vendors.map((v) => (
            <div key={v.id} data-testid={`vendor-row-${v.name}`} className="md:grid md:grid-cols-12 gap-4 px-4 py-4 md:py-5 md:items-center border-b border-white/10 hover:bg-white/[0.02]">
              <div className="flex md:hidden items-start justify-between">
                <div className="min-w-0">
                  <div className="text-white text-sm">{v.name}</div>
                  <div className="font-mont text-xs text-neutral-400 mt-1 truncate">{v.email || "—"}</div>
                  <div className="font-mont text-xs text-neutral-400">{v.phone || "—"}</div>
                </div>
                <div className="flex gap-3 flex-shrink-0 pl-3">
                  <button onClick={() => openEdit(v)} className="text-neutral-500 hover:text-white p-1"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                  <button onClick={() => remove(v.id)} className="text-neutral-500 hover:text-red-400 p-1"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                </div>
              </div>

              <div className="hidden md:block col-span-4 text-white text-sm">{v.name}</div>
              <div className="hidden md:block col-span-3 font-mont text-xs text-neutral-400 truncate">{v.email}</div>
              <div className="hidden md:block col-span-3 font-mont text-xs text-neutral-400">{v.phone}</div>
              <div className="hidden md:flex col-span-2 justify-end gap-3">
                <button onClick={() => openEdit(v)} className="text-neutral-500 hover:text-white"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                <button onClick={() => remove(v.id)} className="text-neutral-500 hover:text-red-400"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
